import { useEffect, useRef } from 'react';
import {
  useAgentContainer,
  type AgentModuleConfig,
} from '@/context/agent-container.context';

/**
 * Register module context for the global chat agent renderer.
 *
 * Callbacks (getModuleContext, renderToolResult) and headerIcon are
 * stored via refs so the renderer always uses the latest version
 * without triggering re-registration on every render.
 */
export function useAgentModule(config: AgentModuleConfig): void {
  const { setModuleConfig, clearModuleConfig } = useAgentContainer();

  const getModuleContextRef = useRef(config.getModuleContext);
  getModuleContextRef.current = config.getModuleContext;

  const renderToolResultRef = useRef(config.renderToolResult);
  renderToolResultRef.current = config.renderToolResult;

  const headerIconRef = useRef(config.headerIcon);
  headerIconRef.current = config.headerIcon;

  const toolsRef = useRef(config.tools);
  toolsRef.current = config.tools;

  const onFocusNodesRef = useRef(config.onFocusNodes);
  onFocusNodesRef.current = config.onFocusNodes;

  useEffect(() => {
    setModuleConfig({
      ...config,
      headerIcon: headerIconRef.current,
      getModuleContext: (...args) => getModuleContextRef.current?.(...args),
      renderToolResult: (...args) => renderToolResultRef.current?.(...args),
      tools: toolsRef.current,
      onFocusNodes: (...args) => onFocusNodesRef.current?.(...args),
    });

    return () => clearModuleConfig();
    // Re-register when identity-stable values change. headerIcon and callbacks
    // are ref-wrapped so they never need to re-register. `tools` is re-read on
    // change so dynamically (un)registered tools — e.g. a step's
    // useDynamicStepState tool that appears when its config panel opens — reach
    // the agent. Callers must pass a memoized tools array (all do).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.moduleKey,
    config.pageKey,
    config.currentPage,
    config.placeholder,
    config.suggestions,
    config.headerTitle,
    config.tools,
    setModuleConfig,
    clearModuleConfig,
  ]);
}
